import React, { useState } from 'react';
import {
  ACCENT,
  ACCENT_DARK,
  ACCENT_BORDER,
  ACCENT_LIGHT,
  CARD,
  TEXT_DARK,
  TEXT_MID,
  FONT,
} from '../lib/tokens';
import { Ic } from '../components/Icons';
import { ScreenWrap, SectionLabel } from '../components/ui/Primitives';
import { useSchedule } from '../lib/useSchedule';
import { useBreakpoint } from '../lib/useBreakpoint';

// Color palette per session kind. session/flex share the primary blue; joint
// uses teal so the Student Lunch reads as a separate-track activity.
const KIND_STYLES = {
  session: {
    accent: ACCENT,
    accentDark: ACCENT_DARK,
    light: ACCENT_LIGHT,
    border: ACCENT_BORDER,
  },
  flex: {
    accent: ACCENT,
    accentDark: ACCENT_DARK,
    light: ACCENT_LIGHT,
    border: ACCENT_BORDER,
  },
  joint: {
    accent: '#0d9488',
    accentDark: '#0f766e',
    light: 'rgba(13,148,136,0.12)',
    border: 'rgba(13,148,136,0.32)',
  },
};

const DAY_LABELS = ['Wed · May 20', 'Thu · May 21', 'Fri · May 22'];

export default function ScheduleScreen({ activeDay, setActiveDay }) {
  const [expanded, setExpanded] = useState(null);
  const [copied, setCopied] = useState(null);
  const { schedule, current } = useSchedule();
  const bp = useBreakpoint();

  const copyPrompt = (id, text) => {
    if (navigator.clipboard) navigator.clipboard.writeText(text).catch(() => {});
    setCopied(id);
    setTimeout(() => setCopied(null), 2000);
  };

  const items = schedule[activeDay] || [];
  const isArrivalDay = activeDay === 0;
  const isDesktop = bp === 'desktop';

  // The schedule context ticks every second to keep the countdown fresh, so
  // this component re-renders constantly. Wrapper components MUST be stable
  // (declared outside the render path or referenced as stable identifiers) —
  // otherwise React unmounts/remounts the subtree each tick and the scroll
  // position resets. Use `OuterWrap` from module scope here.
  const OuterWrap = isDesktop ? DesktopScheduleWrap : ScreenWrap;

  return (
    <OuterWrap>
      <div
        style={{
          display: 'flex',
          background: 'rgba(255,255,255,0.06)',
          borderRadius: 10,
          padding: 3,
          gap: 3,
        }}
      >
        {DAY_LABELS.map((d, i) => (
          <button
            key={i}
            onClick={() => {
              setActiveDay(i);
              setExpanded(null);
            }}
            style={{
              flex: 1,
              padding: '8px 0',
              background: activeDay === i ? ACCENT : 'transparent',
              border: 'none',
              borderRadius: 8,
              color: activeDay === i ? '#fff' : 'rgba(255,255,255,0.4)',
              fontSize: 12,
              fontWeight: 600,
              cursor: 'pointer',
              letterSpacing: '0.01em',
              transition: 'all 0.15s',
              fontFamily: FONT,
            }}
          >
            {d}
          </button>
        ))}
      </div>

      {isArrivalDay && <SectionLabel>ARRIVAL DAY · NO FORMAL SESSIONS</SectionLabel>}

      {items.map((item, idx) => {
        if (item.type === 'break' || item.type === 'note') {
          return (
            <div
              key={`${item.type}-${idx}`}
              style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '4px 2px' }}
            >
              <div style={{ flex: 1, height: 1, background: 'rgba(255,255,255,0.08)' }} />
              <span
                style={{
                  fontSize: 11,
                  color: 'rgba(255,255,255,0.32)',
                  fontWeight: 500,
                  whiteSpace: 'nowrap',
                }}
              >
                {item.time} · {item.label}
              </span>
              <div style={{ flex: 1, height: 1, background: 'rgba(255,255,255,0.08)' }} />
            </div>
          );
        }

        const isExpanded = expanded === item.id;
        const isCurrent = current?.id === item.id;
        const kind = item.kind || 'session';
        const palette = KIND_STYLES[kind] || KIND_STYLES.session;
        const hasPrompt = !!item.prompt;

        const badgeLabel = abbreviate(item.num);
        // Use the row's own `num` so multiple joint/flex sessions can
        // coexist with distinct labels (e.g. "JOINT SESSION" for the
        // student lunch vs. "GROUP DINNER" for Yard House; "OPEN
        // DISCUSSION" vs. "CAMPUS TOUR").
        const sectionLabel = (item.num || '').toUpperCase();

        return (
          <div
            key={item.id}
            style={{
              background: isCurrent
                ? `linear-gradient(135deg, ${palette.accent}18, ${palette.accentDark}10)`
                : CARD,
              border: isCurrent ? `1.5px solid ${palette.border}` : '1.5px solid transparent',
              borderRadius: 13,
              overflow: 'hidden',
              boxShadow: '0 1px 8px rgba(0,0,0,0.08)',
              flexShrink: 0,
            }}
          >
            <button
              onClick={() => setExpanded(isExpanded ? null : item.id)}
              style={{
                width: '100%',
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                padding: '13px 15px',
                display: 'flex',
                alignItems: 'center',
                gap: 12,
                fontFamily: FONT,
                textAlign: 'left',
              }}
            >
              <div
                style={{
                  width: 36,
                  height: 36,
                  borderRadius: 9,
                  flexShrink: 0,
                  background: isCurrent ? palette.accent : palette.light,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <span
                  style={{
                    fontSize: 9.5,
                    fontWeight: 700,
                    color: isCurrent ? '#fff' : palette.accent,
                    letterSpacing: '0.04em',
                    lineHeight: 1.2,
                    textAlign: 'center',
                  }}
                >
                  {badgeLabel}
                </span>
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div
                  style={{
                    fontSize: 10,
                    fontWeight: 700,
                    color: isCurrent ? '#fff' : '#94a3b8',
                    letterSpacing: '0.07em',
                    marginBottom: 2,
                  }}
                >
                  {sectionLabel}
                  {isCurrent ? ' · NOW' : ''}
                </div>
                <div
                  style={{
                    fontSize: 14,
                    fontWeight: 600,
                    color: isCurrent ? '#fff' : TEXT_DARK,
                    lineHeight: 1.3,
                  }}
                >
                  {item.title}
                </div>
                <div
                  style={{
                    fontSize: 11,
                    color: isCurrent ? 'rgba(255,255,255,0.6)' : '#94a3b8',
                    marginTop: 2,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 3,
                  }}
                >
                  <Ic.Clock size={10} color={isCurrent ? 'rgba(255,255,255,0.6)' : '#94a3b8'} /> {item.time} · {item.duration}
                </div>
              </div>
              {isExpanded ? (
                <Ic.ChevronUp size={15} color={isCurrent ? 'rgba(255,255,255,0.7)' : '#94a3b8'} />
              ) : (
                <Ic.ChevronDown size={15} color={isCurrent ? 'rgba(255,255,255,0.7)' : '#94a3b8'} />
              )}
            </button>

            {isExpanded && (
              <div
                style={{
                  padding: '0 15px 14px',
                  borderTop: `1px solid ${isCurrent ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.06)'}`,
                }}
              >
                <p
                  style={{
                    fontSize: 13,
                    color: isCurrent ? 'rgba(255,255,255,0.75)' : TEXT_MID,
                    lineHeight: 1.55,
                    margin: '10px 0 12px',
                  }}
                >
                  {item.desc}
                </p>
                {hasPrompt && (
                  <div
                    style={{
                      background: '#f8fafc',
                      border: '1px solid #e2e8f0',
                      borderRadius: 9,
                      padding: '11px 13px',
                    }}
                  >
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        marginBottom: 6,
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <div
                          style={{
                            width: 18,
                            height: 18,
                            borderRadius: 5,
                            background: palette.accent,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                        >
                          <Ic.Spark size={10} color="#fff" />
                        </div>
                        <span
                          style={{
                            fontSize: 11,
                            fontWeight: 700,
                            color: palette.accent,
                            letterSpacing: '0.05em',
                          }}
                        >
                          {item.promptLabel || 'AI WORKSHOP PROMPT'}
                        </span>
                      </div>
                      <button
                        onClick={() => copyPrompt(item.id, item.prompt)}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 4,
                          background: copied === item.id ? '#dcfce7' : '#fff',
                          border: `1px solid ${copied === item.id ? '#86efac' : '#e2e8f0'}`,
                          borderRadius: 6,
                          padding: '4px 9px',
                          cursor: 'pointer',
                          fontFamily: FONT,
                          fontSize: 11,
                          fontWeight: 600,
                          color: copied === item.id ? '#16a34a' : '#64748b',
                          transition: 'all 0.15s',
                        }}
                      >
                        {copied === item.id ? (
                          <Ic.Check size={11} color="#16a34a" />
                        ) : (
                          <Ic.Copy size={11} color="#64748b" />
                        )}
                        {copied === item.id ? 'Copied!' : 'Copy'}
                      </button>
                    </div>
                    <p
                      style={{
                        fontSize: 12.5,
                        color: '#475569',
                        lineHeight: 1.55,
                        margin: 0,
                        whiteSpace: 'pre-wrap',
                      }}
                    >
                      {item.prompt}
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}
    </OuterWrap>
  );
}

// Module-scope component so its identity is stable across re-renders. Defining
// this inside ScheduleScreen would unmount the entire scrollable subtree on
// every render — which the per-second countdown tick triggers — and reset the
// scroll position to the top.
function DesktopScheduleWrap({ children }) {
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 10,
        maxWidth: 820,
        width: '100%',
        margin: '0 auto',
      }}
    >
      {children}
    </div>
  );
}

function abbreviate(num) {
  if (!num) return '';
  if (num === 'Closing') return 'CL';
  if (num === 'Joint Session') return 'JS';
  if (num === 'Open Discussion') return 'OD';
  if (num === 'Countdown') return 'CD';
  if (num === 'Welcome') return 'WL';
  if (num === 'Campus Tour') return 'CT';
  if (num === 'Group Dinner') return 'GD';
  return num.replace('Session ', 'S');
}
